import type { Hit } from '@algolia/client-search';
import type { PharmacyType } from '@libs/core';
import type { EligiblePromotion } from '@libs/common-types';

export type DisplayType = 'single' | 'range' | undefined;

export type StringifiedBoolean = 'TRUE' | 'FALSE' | 'true' | 'false';

export { PharmacyType } from '@libs/core';

export type ProductHitPrice = {
    displayType: DisplayType;
    formatted: {
        primary: string;
        strikethrough?: string;
    };
    number: number;
    'strikethrough-number'?: number; // TODO: update this once this ticket is done: https://petsmart.atlassian.net/browse/CEC-1667
    validUntil?: string;
};

export type ProductHitImages = {
    large: string;
    small: string;
};

export type ProductHitVideo = {
    posterImage: string;
    sizes: {
        small?: string;
        medium?: string;
        large?: string;
    };
};

export type ProductHitMonthlySales = {
    conversionRate?: number;
    ordersPlaced?: number;
    revenue?: number;
    unitsSold?: number;
};

export type ProductCategory = {
    id: string;
    name: {
        default: string;
    };
};

export type ProductCategories = ProductCategory[][];

export type ProductSize = {
    petSize?: string;
    twoDimensionalSize?: string;
    threeDimensionalSize?: string;
    solidSize?: string;
    fluidSize?: string;
};

export type ProductFlavor = {
    details?: string;
    value?: string;
};

export type ProductHit = Hit<{
    __position: number;
    __queryID?: string;
    badges?: string[];
    bopisCaseQty?: number;
    brand?: string;
    bvAverageRating?: number;
    bvReviewCount?: number;
    categories?: ProductCategories;
    categoryList?: ProductCategory[];
    color?: {
        families: string;
        swatchDisplay: string;
        value: string;
    };
    customBvAverageRating?: number;
    customCaseQuantity?: number | null;
    customCategory?: string[];
    customMoreWaystoShop?: string[];
    customPet?: string[];
    customPharmacyColdStorage?: boolean | StringifiedBoolean; // TODO: remove StringifiedBoolean once this is updated in Algolia.
    customPharmacyOOS?: boolean | StringifiedBoolean; // TODO: remove StringifiedBoolean once this is updated in Algolia.
    customTheme?: string[];
    custom_categories?: string[];
    custom_category_names?: string[];
    normalized_custom_category_names?: string[];
    discontinued?: boolean;
    flavor?: {
        displays: string;
        value: string;
    };
    in_stock?: boolean;
    hasPrice?: boolean;
    hasVideo?: boolean;
    id: number;
    images?: ProductHitImages;
    alternateImages?: string[];
    video?: ProductHitVideo;
    isCaseRequiredSTH?: boolean;
    isCustomizationMandatory?: boolean;
    isLivePet?: boolean;
    isSKUAvailable?: boolean;
    isSearchableFlag?: boolean;
    isSubscriptionEnabled?: boolean;
    long_description?: string;
    masterProductID?: number;
    meta?:
        | {
              title?: string;
              description?: string;
          }
        | string;
    merchandisingHierarchy?: {
        category: string;
        class: string;
        department: string;
        division: string;
        vicePresidentId: number;
        vicePresidentName: string;
    };
    monthlySalesDataRounded?: ProductHitMonthlySales;
    name?: string;
    objectID?: string;
    onlineFrom?: number;
    onlineTo?: number;
    ordersPlaced?: number;
    personalization?: {
        customizationType?: string;
        fontFaceOptions?: string[];
        styleOptions?: {
            styleFinishes?: string[];
            threadColors?: string[];
        };
        templateDescription?: string;
        templateId?: number;
        templateName?: string;
        textLocations?: {
            backLines?: number[];
            frontLines?: number[];
        };
    };
    'pharmacy-type'?: PharmacyType;
    isPoBoxRestricted?: boolean;
    statesRestrictionEnabled?: boolean;
    postalCodeRestrictionEnabled?: boolean;
    price?: ProductHitPrice;
    primary_category_id?: string;
    primary_category_name?: string;
    quantity?: string;
    revenue?: number;
    searchable?: boolean;
    short_description?: string;
    size?: { [key: string]: string };
    sizeChartImgUrl?: string;
    'shoppingOptions-isInStoreOnly'?: boolean;
    'shipToHome-isCASupported'?: boolean;
    'shoppingOptions-isBopisEligible'?: boolean;
    'shipToHome-isCaseRequired-us'?: boolean;
    'shipToHome-isCaseRequired-ca'?: boolean;
    'shoppingOptions-isInStoreOnlyOverride'?: boolean;
    'shoppingOptions-isSoldOnlineOnly'?: boolean;
    customScheduledDeliveryEligible?: boolean;
    unitsSold?: number;
    upc?: string;
    url?: string;
    variations?: string[];
    wattage?: string;
    title?: string;
    prices?: (string | number)[];
    sku?: number;
    eligiblePromotions?: EligiblePromotion[];
    lastModifiedMillis?: number;
}>;

export type StoreKeys = `store_${number | string}`;

export type InventoryHitStore = {
    id: number;
    qty: number;
};

export type InventoryHit = Hit<
    {
        [key in StoreKeys]?: number;
    } & { objectID: string; sku: number }
>;

export type CategoryHit = {
    objectID: string;
    categoryKey: string;
    custom_category_name: string;
    normalized_custom_category_name: string;
    startDate: number;
    endDate: number;
};
